import React, { useState, useEffect } from 'react';
import { 
  Search, Edit2, Lock, Unlock, Building2, 
  Users, MessageSquare, X, Eye, Mail, Phone, 
  MapPin, CreditCard, Smartphone, Ban, RotateCcw,
  Key, AlertTriangle, Clock, CheckCircle2, Shield,
  Plus, Copy
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const LANGUAGES = [
  { code: 'pt-BR', name: '🇧🇷 Português', flag: '🇧🇷' },
  { code: 'en-US', name: '🇺🇸 English', flag: '🇺🇸' },
  { code: 'es-ES', name: '🇪🇸 Español', flag: '🇪🇸' }
];

const statusColors = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700'
};

const statusLabels = {
  active: 'Ativa',
  suspended: 'Suspensa',
  cancelled: 'Cancelada'
};

const planOptions = {
  basic: { label: 'Básico', value: 460, max_users: 5, max_sectors: 5, whatsapp_limit: 3 },
  professional: { label: 'Profissional', value: 997, max_users: 15, max_sectors: 10, whatsapp_limit: 5 },
  enterprise: { label: 'Empresarial', value: 2997, max_users: 50, max_sectors: 20, whatsapp_limit: 10 }
};

function MasterTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    plan_type: '',
    search: ''
  });
  
  // Estados para criação
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    cnpj: '',
    responsible_name: '',
    responsible_email: '',
    phone: '',
    address: '',
    plan_type: 'basic'
  });
  const [creating, setCreating] = useState(false);
  const [createdTenant, setCreatedTenant] = useState(null); // Para mostrar código após criar

  // Estados para edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Estados para visualização
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTenant, setViewingTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Estados para cancelamento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingTenant, setCancellingTenant] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Estados para reativação
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivatingTenant, setReactivatingTenant] = useState(null);

  // Estados para reset de senha
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTenant, setPasswordTenant] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    loadTenants();
  }, [filters]);

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(filters).toString();
      const res = await axios.get(`${API}/api/master-admin/tenants?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      setLoading(false);
    }
  };

  // ============================================
  // ⭐ NOVO: CRIAR EMPRESA
  // ============================================
  const handleCreateClick = () => {
    setCreateForm({
      name: '',
      cnpj: '',
      responsible_name: '',
      responsible_email: '',
      phone: '',
      address: '',
      plan_type: 'basic'
    });
    setCreatedTenant(null);
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    if (!createForm.name.trim() || !createForm.responsible_name.trim() || !createForm.responsible_email.trim()) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/master-admin/tenants`,
        createForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setCreatedTenant(res.data.tenant);
        loadTenants();
        // Não fechar o modal, mostrar o código de convite
      }
    } catch (err) {
      console.error('Erro ao criar empresa:', err);
      alert('Erro ao criar empresa: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  const copyInvitationCodeToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    alert('✅ Código copiado: ' + code);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreatedTenant(null);
    setCreateForm({
      name: '',
      cnpj: '',
      responsible_name: '',
      responsible_email: '',
      phone: '',
      address: '',
      plan_type: 'basic'
    });
  };

  // ============================================
  // VISUALIZAR EMPRESA
  // ============================================
  const handleView = async (tenant) => {
    setViewingTenant(tenant);
    setShowViewModal(true);
    setLoadingDetails(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const [usersRes, numbersRes, sectorsRes] = await Promise.all([
  axios.get(`${API}/api/master-admin/tenants/${tenant.id}/users`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  axios.get(`${API}/api/master-admin/tenants/${tenant.id}/phone-numbers`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  axios.get(`${API}/api/master-admin/tenants/${tenant.id}/sectors`, {
    headers: { Authorization: `Bearer ${token}` }
  })
]);
      
      setTenantDetails({
        users: usersRes.data,
        phoneNumbers: numbersRes.data,
        sectors: sectorsRes.data
      });
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setTenantDetails({ users: [], phoneNumbers: [], sectors: [] });
    } finally {
      setLoadingDetails(false);
    }
  };

  // ============================================
  // EDITAR EMPRESA
  // ============================================
  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      cnpj: tenant.cnpj || '',
      responsible_name: tenant.responsible_name || '',
      responsible_email: tenant.responsible_email || '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      plan_type: tenant.plan_type,
      plan_value: tenant.plan_value,
      max_users: tenant.max_users,
      max_sectors: tenant.max_sectors,
      whatsapp_numbers_limit: tenant.whatsapp_numbers_limit,
      default_language: tenant.default_language || 'pt-BR'
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/master-admin/tenants/${editingTenant.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditModal(false);
      setEditingTenant(null);
      loadTenants();
      alert('Empresa atualizada com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar:', err);
      alert('Erro ao atualizar empresa: ' + (err.response?.data?.error || err.message));
    }
  };

  // ============================================
  // ALTERAR PLANO
  // ============================================
  const handleChangePlan = async (tenant, newPlan) => {
    if (!window.confirm(`Alterar plano de ${tenant.plan_type} para ${newPlan}?`)) return;

    const plan = planOptions[newPlan];
    if (!plan) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/master-admin/tenants/${tenant.id}/change-plan`,
        {
          plan_type: newPlan,
          plan_value: plan.value,
          max_users: plan.max_users,
          max_sectors: plan.max_sectors,
          whatsapp_numbers_limit: plan.whatsapp_limit
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadTenants();
      alert('Plano alterado com sucesso!');
    } catch (err) {
      console.error('Erro ao alterar plano:', err);
      alert('Erro ao alterar plano: ' + (err.response?.data?.error || err.message));
    }
  };

  // ============================================
  // SUSPENDER/ATIVAR
  // ============================================
  const handleToggleStatus = async (tenant, newStatus) => {
    const reason = newStatus === 'suspended' 
      ? window.prompt('Motivo da suspensão:') 
      : null;
    
    if (newStatus === 'suspended' && !reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/master-admin/tenants/${tenant.id}/toggle-status`,
        { status: newStatus, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadTenants();
      alert(`Empresa ${newStatus === 'active' ? 'ativada' : 'suspensa'} com sucesso!`);
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      alert('Erro ao alterar status: ' + (err.response?.data?.error || err.message));
    }
  };

  // ============================================
  // CANCELAR EMPRESA
  // ============================================
  const handleCancelClick = (tenant) => {
    setCancellingTenant(tenant);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason.trim()) {
      alert('Por favor, informe o motivo do cancelamento');
      return;
    }

    if (!window.confirm(
      `⚠️ ATENÇÃO: Você está prestes a CANCELAR a empresa "${cancellingTenant.name}".\n\n` +
      `Esta ação irá:\n` +
      `• Bloquear o acesso de todos os usuários\n` +
      `• Invalidar todas as sessões ativas\n` +
      `• Desativar todos os usuários da empresa\n\n` +
      `Deseja continuar?`
    )) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/master-admin/tenants/${cancellingTenant.id}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowCancelModal(false);
      setCancellingTenant(null);
      setCancelReason('');
      loadTenants();
      alert('Empresa cancelada com sucesso!');
    } catch (err) {
      console.error('Erro ao cancelar:', err);
      alert('Erro ao cancelar empresa: ' + (err.response?.data?.error || err.message));
    }
  };

  // ============================================
  // REATIVAR EMPRESA
  // ============================================
  const handleReactivateClick = (tenant) => {
    setReactivatingTenant(tenant);
    setShowReactivateModal(true);
  };

  const handleReactivateConfirm = async () => {
    if (!window.confirm(
      `Reativar a empresa "${reactivatingTenant.name}"?\n\n` +
      `Todos os usuários serão reativados e poderão fazer login novamente.`
    )) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/master-admin/tenants/${reactivatingTenant.id}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowReactivateModal(false);
      setReactivatingTenant(null);
      loadTenants();
      alert('Empresa reativada com sucesso!');
    } catch (err) {
      console.error('Erro ao reativar:', err);
      alert('Erro ao reativar empresa: ' + (err.response?.data?.error || err.message));
    }
  };

  // ============================================
  // RESETAR SENHA DO ADMIN
  // ============================================
  const handleResetPasswordClick = (tenant) => {
    setPasswordTenant(tenant);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(true);
    setShowPasswordModal(true);
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setConfirmPassword(password);
  };

  const handleResetPasswordConfirm = async () => {
    if (newPassword.length < 6) {
      alert('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('As senhas não coincidem');
      return;
    }

    if (!window.confirm(
      `Redefinir a senha do administrador da empresa "${passwordTenant.name}"?\n\n` +
      `O administrador será notificado por email com a nova senha e terá que fazer login novamente.`
    )) return;

    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/master-admin/tenants/${passwordTenant.id}/reset-admin-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPasswordModal(false);
      setPasswordTenant(null);
      setNewPassword('');
      setConfirmPassword('');
      alert('✅ Senha redefinida com sucesso!\n\nO administrador foi notificado por email.');
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      alert('Erro ao resetar senha: ' + (err.response?.data?.error || err.message));
    } finally {
      setResettingPassword(false);
    }
  };

  // ============================================
  // COPIAR CÓDIGO DE CONVITE
  // ============================================
  const copyInvitationCode = (code) => {
    navigator.clipboard.writeText(code);
    alert('Código copiado: ' + code);
  };

  // ============================================
  // CALCULAR DIAS SUSPENSO
  // ============================================
  const getDaysSuspended = (suspendedAt) => {
    if (!suspendedAt) return 0;
    const suspended = new Date(suspendedAt);
    const now = new Date();
    const diffTime = Math.abs(now - suspended);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 size={28} className="text-orange-600" />
            Gestão de Empresas
          </h1>
          <p className="text-gray-500 mt-1">Gerencie todas as empresas do sistema</p>
        </div>
        
        {/* ⭐ BOTÃO ADICIONAR NOVA EMPRESA */}
        <button
          onClick={handleCreateClick}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-200 transition-all"
        >
          <Plus size={20} />
          Adicionar Nova Empresa
        </button>
      </div>

      {/* Aviso sobre cancelamento automático */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
        <Clock size={20} className="text-orange-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-orange-900">
            Cancelamento Automático
          </p>
          <p className="text-xs text-orange-700 mt-1">
            Empresas suspensas por mais de <strong>30 dias</strong> são automaticamente canceladas pelo sistema.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar empresa..."
              value={filters.search}
              onChange={e => setFilters({...filters, search: e.target.value})}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all"
            />
          </div>
          <select
            value={filters.status}
            onChange={e => setFilters({...filters, status: e.target.value})}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all"
          >
            <option value="">Todos os status</option>
            <option value="active">Ativas</option>
            <option value="suspended">Suspensas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          <select
            value={filters.plan_type}
            onChange={e => setFilters({...filters, plan_type: e.target.value})}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all"
          >
            <option value="">Todos os planos</option>
            <option value="basic">Básico</option>
            <option value="professional">Profissional</option>
            <option value="enterprise">Empresarial</option>
          </select>
        </div>
      </div>

      {/* Lista de Empresas */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 text-xs uppercase font-semibold">
              <tr>
                <th className="px-6 py-4">Empresa</th>
                <th className="px-6 py-4">Plano</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Idioma</th>
                <th className="px-6 py-4">Usuários</th>
                <th className="px-6 py-4">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-3 text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                      <span>Carregando...</span>
                    </div>
                  </td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    <Building2 size={48} className="mx-auto mb-3 opacity-30" />
                    <p className="text-lg font-medium">Nenhuma empresa encontrada</p>
                  </td>
                </tr>
              ) : (
                tenants.map(tenant => {
                  const daysSuspended = tenant.status === 'suspended' 
                    ? getDaysSuspended(tenant.suspended_at) 
                    : 0;
                  const daysUntilCancel = 30 - daysSuspended;
                  
                  return (
                    <tr key={tenant.id} className={`hover:bg-gray-50 transition-colors ${
                      tenant.status === 'cancelled' ? 'bg-red-50/30' : ''
                    }`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 text-white rounded-full flex items-center justify-center font-bold shadow-sm ${
                            tenant.status === 'cancelled' 
                              ? 'bg-gradient-to-br from-red-400 to-red-600'
                              : tenant.status === 'suspended'
                              ? 'bg-gradient-to-br from-orange-400 to-orange-600'
                              : 'bg-gradient-to-br from-orange-400 to-orange-600'
                          }`}>
                            {tenant.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{tenant.name}</p>
                            <p className="text-xs text-gray-500">{tenant.responsible_email}</p>
                            {tenant.cnpj && (
                              <p className="text-xs text-gray-400">CNPJ: {tenant.cnpj}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={tenant.plan_type}
                          onChange={e => handleChangePlan(tenant, e.target.value)}
                          disabled={tenant.status === 'cancelled'}
                          className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-blue-700 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="basic">Básico</option>
                          <option value="professional">Profissional</option>
                          <option value="enterprise">Empresarial</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          R$ {tenant.plan_value}/mês
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${statusColors[tenant.status]}`}>
                            {statusLabels[tenant.status]}
                          </span>
                          {tenant.status === 'suspended' && (
                            <div className="mt-2">
                              {daysUntilCancel > 0 ? (
                                <p className="text-xs text-orange-600 flex items-center gap-1">
                                  <Clock size={12} />
                                  Cancela em {daysUntilCancel} dias
                                </p>
                              ) : (
                                <p className="text-xs text-red-600 flex items-center gap-1 font-bold">
                                  <AlertTriangle size={12} />
                                  Cancelamento iminente
                                </p>
                              )}
                            </div>
                          )}
                          {tenant.status === 'cancelled' && tenant.cancelled_at && (
                            <p className="text-xs text-red-500 mt-1">
                              Cancelada em {new Date(tenant.cancelled_at).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                      </td>
<td className="px-6 py-4">
  <span className="text-lg" title={tenant.default_language || 'pt-BR'}>
    {LANGUAGES.find(l => l.code === (tenant.default_language || 'pt-BR'))?.flag || '🇧🇷'}
  </span>
</td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users size={16} className="text-gray-400" />
                          <span className="font-medium">{tenant.users_count || 0}</span>
                          <span className="text-gray-400">/</span>
                          <span className="text-gray-500">{tenant.max_users}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => handleView(tenant)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Visualizar Detalhes"
                          >
                            <Eye size={18} />
                          </button>

                          {tenant.status !== 'cancelled' && (
                            <button
                              onClick={() => handleEdit(tenant)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Editar Empresa"
                            >
                              <Edit2 size={18} />
                            </button>
                          )}

                          <button
                            onClick={() => handleResetPasswordClick(tenant)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Resetar Senha do Admin"
                          >
                            <Key size={18} />
                          </button>

                          {tenant.status === 'active' && (
                            <>
                              <button
                                onClick={() => handleToggleStatus(tenant, 'suspended')}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                title="Suspender"
                              >
                                <Lock size={18} />
                              </button>
                              <button
                                onClick={() => handleCancelClick(tenant)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Cancelar Empresa"
                              >
                                <Ban size={18} />
                              </button>
                            </>
                          )}

                          {tenant.status === 'suspended' && (
                            <>
                              <button
                                onClick={() => handleToggleStatus(tenant, 'active')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Ativar"
                              >
                                <Unlock size={18} />
                              </button>
                              <button
                                onClick={() => handleCancelClick(tenant)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Cancelar Empresa"
                              >
                                <Ban size={18} />
                              </button>
                            </>
                          )}

                          {tenant.status === 'cancelled' && (
                            <button
                              onClick={() => handleReactivateClick(tenant)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Reativar Empresa"
                            >
                              <RotateCcw size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================ */}
      {/* ⭐ MODAL DE CRIAÇÃO DE EMPRESA */}
      {/* ============================================ */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Plus size={24} />
                  {createdTenant ? 'Empresa Criada com Sucesso!' : 'Adicionar Nova Empresa'}
                </h2>
                <p className="text-sm opacity-90 mt-1">
                  {createdTenant 
                    ? 'Envie o código de convite para o cliente'
                    : 'Preencha os dados para criar uma nova empresa'}
                </p>
              </div>
              <button
                onClick={closeCreateModal}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Conteúdo */}
            {!createdTenant ? (
              // FORMULÁRIO DE CRIAÇÃO
              <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={e => setCreateForm({...createForm, name: e.target.value})}
                      placeholder="Ex: Cartão da Família"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ (opcional)
                    </label>
                    <input
                      type="text"
                      value={createForm.cnpj}
                      onChange={e => setCreateForm({...createForm, cnpj: e.target.value})}
                      placeholder="00.000.000/0000-00"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone (opcional)
                    </label>
                    <input
                      type="text"
                      value={createForm.phone}
                      onChange={e => setCreateForm({...createForm, phone: e.target.value})}
                      placeholder="(00) 00000-0000"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Responsável *
                    </label>
                    <input
                      type="text"
                      value={createForm.responsible_name}
                      onChange={e => setCreateForm({...createForm, responsible_name: e.target.value})}
                      placeholder="Nome completo"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email do Responsável *
                    </label>
                    <input
                      type="email"
                      value={createForm.responsible_email}
                      onChange={e => setCreateForm({...createForm, responsible_email: e.target.value})}
                      placeholder="admin@empresa.com"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Endereço (opcional)
                    </label>
                    <textarea
                      value={createForm.address}
                      onChange={e => setCreateForm({...createForm, address: e.target.value})}
                      placeholder="Endereço completo"
                      rows={2}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plano *
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(planOptions).map(([key, plan]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setCreateForm({...createForm, plan_type: key})}
                          className={`p-4 border-2 rounded-xl text-left transition-all ${
                            createForm.plan_type === key
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-bold text-gray-900">{plan.label}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            R$ {plan.value}/mês
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {plan.max_users} usuários
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-900">
                    <strong>ℹ️ Como funciona:</strong> Após criar a empresa, um código de convite será gerado automaticamente. 
                    Envie este código para o cliente, que poderá criar sua conta de administrador.
                  </p>
                </div>

                <div className="flex gap-3 justify-end pt-2">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    className="px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-bold hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all shadow-lg shadow-orange-200 flex items-center gap-2"
                  >
                    {creating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Criando...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Criar Empresa
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              // ⭐ EXIBIÇÃO DO CÓDIGO DE CONVITE
              <div className="p-6 space-y-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Empresa "{createdTenant.name}" criada com sucesso!
                  </h3>
                  <p className="text-sm text-gray-600">
                    Plano: <strong>{planOptions[createdTenant.plan_type]?.label}</strong> - R$ {createdTenant.plan_value}/mês
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-300 rounded-2xl p-6">
                  <div className="text-center mb-4">
                    <Key size={32} className="text-orange-600 mx-auto mb-2" />
                    <h3 className="text-lg font-bold text-gray-900">
                      Código de Convite
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Envie este código para o administrador da empresa
                    </p>
                  </div>

                  <div className="bg-white border-2 border-dashed border-orange-400 rounded-xl p-6 text-center">
                    <p className="text-4xl font-mono font-bold text-orange-600 tracking-widest">
                      {createdTenant.invitation_code}
                    </p>
                  </div>

                  <button
                    onClick={() => copyInvitationCodeToClipboard(createdTenant.invitation_code)}
                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg"
                  >
                    <Copy size={18} />
                    Copiar Código
                  </button>

                  <div className="mt-4 bg-white/70 rounded-xl p-4">
                    <p className="text-xs text-gray-700">
                      <strong>📧 Próximos passos:</strong>
                    </p>
                    <ol className="text-xs text-gray-600 mt-2 space-y-1 list-decimal list-inside">
                      <li>Envie este código para <strong>{createdTenant.responsible_email}</strong></li>
                      <li>O cliente acessa a página de registro</li>
                      <li>Digita o código e cria sua conta de administrador</li>
                      <li>Pronto! A empresa estará ativa no sistema</li>
                    </ol>
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={closeCreateModal}
                    className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl text-sm font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-200"
                  >
                    Concluir
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DE CANCELAMENTO */}
      {/* ============================================ */}
      {showCancelModal && cancellingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-700 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Ban size={28} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Cancelar Empresa</h2>
                  <p className="text-sm opacity-90">{cancellingTenant.name}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-900 font-semibold mb-2">
                  ⚠️ Atenção: Esta ação é irreversível!
                </p>
                <ul className="text-xs text-red-700 space-y-1">
                  <li>• Todos os usuários serão desativados</li>
                  <li>• Todas as sessões serão encerradas</li>
                  <li>• A empresa perderá acesso ao sistema</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo do cancelamento *
                </label>
                <textarea
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="Ex: Inadimplência, solicitação do cliente, etc."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none"
                />
              </div>
            </div>

            <div className="bg-gray-50 p-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingTenant(null);
                  setCancelReason('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={!cancelReason.trim()}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl text-sm font-bold hover:from-red-600 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar Cancelamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DE REATIVAÇÃO */}
      {/* ============================================ */}
      {showReactivateModal && reactivatingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-green-700 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <RotateCcw size={28} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Reativar Empresa</h2>
                  <p className="text-sm opacity-90">{reactivatingTenant.name}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-900">
                  A empresa será reativada e todos os usuários poderão fazer login novamente.
                </p>
              </div>

              {reactivatingTenant.cancellation_reason && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Motivo do cancelamento anterior:</p>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {reactivatingTenant.cancellation_reason}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowReactivateModal(false);
                  setReactivatingTenant(null);
                }}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleReactivateConfirm}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-xl text-sm font-bold hover:from-green-600 hover:to-green-800"
              >
                Confirmar Reativação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DE RESET DE SENHA */}
      {/* ============================================ */}
      {showPasswordModal && passwordTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-purple-700 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Key size={28} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Resetar Senha do Admin</h2>
                  <p className="text-sm opacity-90">{passwordTenant.name}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-sm text-purple-900">
                  <strong>⚠️ Atenção:</strong> O administrador receberá um email com a nova senha e terá que fazer login novamente.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nova Senha
                </label>
                <div className="relative">
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Senha
                </label>
                <input 
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Digite a senha novamente"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-600 mt-1">As senhas não coincidem</p>
                )}
              </div>

              <button
                type="button"
                onClick={generateRandomPassword}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Shield size={16} />
                Gerar Senha Aleatória Segura
              </button>
            </div>

            <div className="bg-gray-50 p-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordTenant(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleResetPasswordConfirm}
                disabled={resettingPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-xl text-sm font-bold hover:from-purple-600 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {resettingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Resetando...
                  </>
                ) : (
                  <>
                    <Key size={16} />
                    Resetar Senha
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DE VISUALIZAÇÃO */}
      {/* ============================================ */}
      {showViewModal && viewingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold backdrop-blur-sm">
                  {viewingTenant.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold">Detalhes da Empresa</h2>
                  <p className="text-sm opacity-90">{viewingTenant.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Building2 size={18} className="text-blue-600" />
                  Informações da Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Nome</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTenant.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">CNPJ</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTenant.cnpj || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Responsável</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTenant.responsible_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTenant.responsible_email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Telefone</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTenant.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Endereço</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTenant.address || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data de Cadastro</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(viewingTenant.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Status</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${statusColors[viewingTenant.status]}`}>
                      {statusLabels[viewingTenant.status]}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-100 rounded-xl p-6">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <CreditCard size={18} className="text-orange-600" />
                  Plano e Código de Convite
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Plano</p>
                    <p className="text-sm font-bold text-orange-600 uppercase">
                      {viewingTenant.plan_type}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Valor Mensal</p>
                    <p className="text-lg font-bold text-gray-900">
                      R$ {Number(viewingTenant.plan_value || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Código de Convite</p>
                    <button
                      onClick={() => copyInvitationCode(viewingTenant.invitation_code)}
                      className="text-sm font-mono font-bold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 mx-auto"
                      title="Clique para copiar"
                    >
                      <Copy size={12} />
                      {viewingTenant.invitation_code}
                    </button>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Status</p>
                    <p className={`text-sm font-bold ${
                      viewingTenant.status === 'active' ? 'text-green-600' :
                      viewingTenant.status === 'suspended' ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {statusLabels[viewingTenant.status]}
                    </p>
                  </div>
                </div>
              </div>

              {loadingDetails && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3 text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span>Carregando detalhes...</span>
                  </div>
                </div>
              )}

              {!loadingDetails && (
                <div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Users size={18} className="text-purple-600" />
                    Usuários Cadastrados ({tenantDetails?.users?.length || 0})
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {tenantDetails?.users && tenantDetails.users.length > 0 ? (
                      <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-700 text-xs uppercase">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Nome</th>
                            <th className="px-4 py-3 font-semibold">Email</th>
                            <th className="px-4 py-3 font-semibold">Função</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {tenantDetails.users.map((user, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                                    {user.name.charAt(0)}
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">{user.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-bold">
                                  {user.role}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                }`}>
                                  {user.is_active ? 'Ativo' : 'Inativo'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-8 text-center text-gray-400">
                        <Users size={32} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Nenhum usuário cadastrado</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 p-6 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingTenant(null);
                  setTenantDetails(null);
                }}
                className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-300 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DE EDIÇÃO */}
      {/* ============================================ */}
      {showEditModal && editingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Edit2 size={24} />
                  Editar Empresa
                </h2>
                <p className="text-sm opacity-90 mt-1">{editingTenant.name}</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ</label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={e => setFormData({...formData, cnpj: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
                  <input
                    type="text"
                    value={formData.responsible_name}
                    onChange={e => setFormData({...formData, responsible_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email do Responsável</label>
                  <input
                    type="email"
                    value={formData.responsible_email}
                    onChange={e => setFormData({...formData, responsible_email: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
                  <select
                    value={formData.plan_type}
                    onChange={e => {
                      const plan = planOptions[e.target.value];
                      setFormData({
                        ...formData,
                        plan_type: e.target.value,
                        plan_value: plan.value,
                        max_users: plan.max_users,
                        max_sectors: plan.max_sectors,
                        whatsapp_numbers_limit: plan.whatsapp_limit
                      });
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  >
                    <option value="basic">Básico</option>
                    <option value="professional">Profissional</option>
                    <option value="enterprise">Empresarial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mensal (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.plan_value}
                    onChange={e => setFormData({...formData, plan_value: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Usuários</label>
                  <input
                    type="number"
                    value={formData.max_users}
                    onChange={e => setFormData({...formData, max_users: parseInt(e.target.value)})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Setores</label>
                  <input
                    type="number"
                    value={formData.max_sectors}
                    onChange={e => setFormData({...formData, max_sectors: parseInt(e.target.value)})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Números WhatsApp</label>
                  <input
                    type="number"
                    value={formData.whatsapp_numbers_limit}
                    onChange={e => setFormData({...formData, whatsapp_numbers_limit: parseInt(e.target.value)})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                {/* ⭐ IDIOMA PADRÃO DA EMPRESA */}
<div className="md:col-span-2">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    🌐 Idioma Padrão da Empresa
  </label>
  <select
    value={formData.default_language}
    onChange={e => setFormData({...formData, default_language: e.target.value})}
    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
  >
    {LANGUAGES.map(lang => (
      <option key={lang.code} value={lang.code}>
        {lang.name}
      </option>
    ))}
  </select>
  <p className="text-xs text-gray-500 mt-1">
    Este idioma será usado como padrão para novos usuários desta empresa
  </p>
</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <textarea
                  value={formData.address}
                  onChange={e => setFormData({...formData, address: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-bold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterTenants;
